import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, Sparkles, Star, Tag } from "lucide-react";

import {
  getNewProducts,
  getBestSellingProducts,
  getDiscountedProducts,
  ProductListDto,
} from "../api/productApi";

const RAW_API_URL = process.env.REACT_APP_API_URL || "";
const API_URL = RAW_API_URL.replace(/\/+$/, "");
const FALLBACK_IMG = "/images/placeholder.jpg";
const HERO_FALLBACK = "/images/hero.png";

type HomeProduct = {
  product_id: number;
  name: string;
  base_price: number;
  image_url: string;
  slug: string;
};

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

function resolveImg(url?: string | null) {
  if (!url) return FALLBACK_IMG;
  const u = String(url).split("\\").join("/").trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return API_URL ? `${API_URL}${u}` : u;
  return API_URL ? `${API_URL}/${u}` : u;
}

function mapProduct(p: ProductListDto): HomeProduct {
  const id = Number(p?.id ?? 0);
  return {
    product_id: id,
    name: String(p?.name ?? ""),
    base_price: Number(p?.salePrice ?? p?.basePrice ?? 0),
    image_url: resolveImg(p?.thumbnail ?? null),
    slug: String(id),
  };
}

function formatPrice(value: number, lang: "vi" | "en") {
  try {
    return new Intl.NumberFormat(lang === "vi" ? "vi-VN" : "en-US", {
      style: "currency",
      currency: "VND",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value.toLocaleString(lang === "vi" ? "vi-VN" : "en-US")}đ`;
  }
}

function useHomeCopy(lang: "vi" | "en") {
  return useMemo(() => {
    if (lang === "vi") {
      return {
        heroBadge: "OWL DREAM / LOCAL BRAND",
        heroTitle: "Identity, not trends.",
        heroDesc: "Streetwear với góc nhìn rõ ràng hơn.",
        heroPrimary: "Vào collection",
        heroSecondary: "Xem drops",
        stats: ["New season", "Curated pieces", "Art-led streetwear"],
        sectionNew: { kicker: "New", title: "Latest pieces", cta: "Xem tất cả" },
        sectionBest: { kicker: "Best seller", title: "Most wanted" },
        sectionSale: { kicker: "Sale", title: "Better price" },
        quick: { new: "New season", best: "Best seller", sale: "Sale selection" },
        productLabel: "OWL DREAM / PIECE",
        heroProductLabel: "New season / Piece",
        viewProduct: "Xem sản phẩm",
        shopAll: "Xem collection",
        finalTitle: "Quiet. Sharp. Distinct.",
        finalPrimary: "Shop now",
        finalSecondary: "View drops",
        emptyTitle: "Chưa có sản phẩm",
        emptyDesc: "Dữ liệu sẽ hiện ở đây khi API trả về.",
        errorTitle: "Không thể tải dữ liệu",
        missingApi: "Thiếu REACT_APP_API_URL nên chưa lấy được dữ liệu sản phẩm.",
      };
    }

    return {
      heroBadge: "OWL DREAM / LOCAL BRAND",
      heroTitle: "Identity, not trends.",
      heroDesc: "Streetwear with a clearer point of view.",
      heroPrimary: "Shop collection",
      heroSecondary: "View drops",
      stats: ["New season", "Curated pieces", "Art-led streetwear"],
      sectionNew: { kicker: "New", title: "Latest pieces", cta: "View all" },
      sectionBest: { kicker: "Best seller", title: "Most wanted" },
      sectionSale: { kicker: "Sale", title: "Better price" },
      quick: { new: "New season", best: "Best seller", sale: "Sale selection" },
      productLabel: "OWL DREAM / PIECE",
      heroProductLabel: "New season / Piece",
      viewProduct: "View product",
      shopAll: "Shop collection",
      finalTitle: "Quiet. Sharp. Distinct.",
      finalPrimary: "Shop now",
      finalSecondary: "View drops",
      emptyTitle: "No products yet",
      emptyDesc: "Product data will appear here once the API responds.",
      errorTitle: "Unable to load data",
      missingApi: "REACT_APP_API_URL is missing, so product data cannot be fetched yet.",
    };
  }, [lang]);
}

function TinyPill(props: { children: React.ReactNode; dark?: boolean; icon?: React.ReactNode }) {
  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-2",
        props.dark ? "border-white/12 bg-white/10" : "border-brand-brown/10 bg-white/78",
      )}
    >
      {props.icon ? <span className="text-brand-gold">{props.icon}</span> : null}
      <span
        className={cn(
          "text-[10px] font-medium uppercase tracking-[0.18em]",
          props.dark ? "text-white/80" : "text-brand-brown/76",
        )}
      >
        {props.children}
      </span>
    </div>
  );
}

function Skeleton(props: { className?: string; dark?: boolean }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-[30px] border",
        props.dark ? "border-white/10 bg-white/10" : "border-brand-brown/10 bg-brand-brown/[0.05]",
        props.className,
      )}
    />
  );
}

function EmptyState(props: { title: string; desc: string; dark?: boolean; className?: string }) {
  return (
    <div
      className={cn(
        "rounded-[30px] border p-8",
        props.dark ? "border-white/10 bg-white/6" : "border-brand-brown/10 bg-white/64",
        props.className,
      )}
    >
      <p className={cn("text-[10px] font-medium uppercase tracking-[0.18em]", props.dark ? "text-white/56" : "text-brand-brown/46")}>{props.title}</p>
      <p className={cn("mt-4 text-sm leading-7", props.dark ? "text-white/74" : "text-brand-brown/64")}>{props.desc}</p>
    </div>
  );
}

function ProductTile(props: {
  item: HomeProduct;
  lang: "vi" | "en";
  label: string;
  viewLabel: string;
  featured?: boolean;
}) {
  const { item, lang, label, viewLabel, featured } = props;

  return (
    <Link to={`/product/${item.slug}`} className={cn("group block", featured && "lg:col-span-2")}> 
      <article className="overflow-hidden rounded-[30px] border border-brand-brown/10 bg-white/72 shadow-[0_24px_80px_rgba(43,31,26,0.08)] transition duration-500 hover:-translate-y-1 hover:shadow-[0_34px_120px_rgba(43,31,26,0.14)]">
        <div className={cn("relative overflow-hidden bg-[#f6f0e6]", featured ? "aspect-[1.08] min-h-[380px] lg:min-h-[520px]" : "aspect-[4/5]") }>
          <img
            src={item.image_url || FALLBACK_IMG}
            alt={item.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
            }}
            loading="lazy"
          />
          <div className="absolute left-4 top-4 sm:left-5 sm:top-5">
            <TinyPill>{label}</TinyPill>
          </div>
        </div>

        <div className={cn("border-t border-brand-brown/8 bg-white/94", featured ? "p-5 sm:p-6 lg:p-7" : "p-4 sm:p-5") }>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/48">OWL DREAM / PIECE</p>
          <h3 className={cn("mt-2 font-medium leading-[1] tracking-[0.01em] text-brand-brown", featured ? "text-[28px] sm:text-[36px]" : "text-[16px] sm:text-[18px]")}>{item.name}</h3>
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-sm font-medium text-brand-brown/80">{formatPrice(item.base_price, lang)}</span>
            <span className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.16em] text-brand-brown/72 transition group-hover:translate-x-1">
              {viewLabel}
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function SplitSpotlight(props: {
  item?: HomeProduct;
  lang: "vi" | "en";
  kicker: string;
  title: string;
  viewLabel: string;
  shopLabel: string;
  dark?: boolean;
  reverse?: boolean;
}) {
  if (!props.item) return null;

  return (
    <div
      className={cn(
        "grid overflow-hidden rounded-[32px] border lg:grid-cols-2",
        props.dark
          ? "border-white/10 bg-brand-brown text-brand-ivory shadow-[0_36px_120px_rgba(0,0,0,0.18)]"
          : "border-brand-brown/10 bg-white/66 text-brand-brown shadow-[0_28px_90px_rgba(43,31,26,0.08)]",
      )}
    >
      <div className={cn("relative min-h-[340px] overflow-hidden bg-[#f6f0e6]", props.reverse && "lg:order-2")}>
        <img
          src={props.item.image_url || FALLBACK_IMG}
          alt={props.item.name}
          className="h-full w-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src = FALLBACK_IMG;
          }}
          loading="lazy"
        />
      </div>

      <div className={cn("flex flex-col justify-center p-8 sm:p-10 lg:p-12", props.reverse && "lg:order-1")}>
        <p className={cn("text-[10px] font-medium uppercase tracking-[0.18em]", props.dark ? "text-white/56" : "text-brand-brown/46")}>{props.kicker}</p>
        <h3 className="mt-4 text-[28px] font-medium leading-[0.98] tracking-[0.01em] sm:text-[36px]">{props.title}</h3>
        <div className={cn("mt-7 border-t pt-5", props.dark ? "border-white/10" : "border-brand-brown/10")}>
          <p className={cn("text-[10px] font-medium uppercase tracking-[0.18em]", props.dark ? "text-white/50" : "text-brand-brown/46")}>{props.kicker}</p>
          <h4 className="mt-3 text-lg font-medium leading-6">{props.item.name}</h4>
          <p className={cn("mt-3 text-sm", props.dark ? "text-white/76" : "text-brand-brown/74")}>{formatPrice(props.item.base_price, props.lang)}</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/product/${props.item.slug}`}
            className={cn(
              "inline-flex h-11 items-center gap-2 rounded-full px-6 text-[11px] font-medium uppercase tracking-[0.14em] transition",
              props.dark ? "bg-white text-brand-brown hover:bg-brand-ivory" : "bg-brand-brown text-brand-ivory hover:opacity-90",
            )}
          >
            {props.viewLabel}
            <ArrowUpRight className="h-4 w-4" />
          </Link>
          <Link
            to="/collection"
            className={cn(
              "inline-flex h-11 items-center rounded-full border px-6 text-[11px] font-medium uppercase tracking-[0.14em] transition",
              props.dark ? "border-white/12 text-white/82 hover:border-white/24 hover:bg-white/8" : "border-brand-brown/12 text-brand-brown/76 hover:border-brand-brown/24 hover:bg-brand-brown/5",
            )}
          >
            {props.shopLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function Home() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "vi";
  const copy = useHomeCopy(lang);

  const [products, setProducts] = useState<HomeProduct[]>([]);
  const [bestSelling, setBestSelling] = useState<HomeProduct[]>([]);
  const [discounted, setDiscounted] = useState<HomeProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canFetch = useMemo(() => !!API_URL, []);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function loadHome() {
      setLoading(true);
      setError(null);

      if (!canFetch) {
        setProducts([]);
        setBestSelling([]);
        setDiscounted([]);
        setLoading(false);
        return;
      }

      try {
        const [newList, bestList, discList] = await Promise.all([
          getNewProducts(signal),
          getBestSellingProducts(signal),
          getDiscountedProducts(signal),
        ]);

        setProducts((newList || []).map(mapProduct));
        setBestSelling((bestList || []).map(mapProduct));
        setDiscounted((discList || []).map(mapProduct));
      } catch (err: any) {
        if (err?.name === "AbortError") return;
        setError(err?.message || copy.errorTitle);
      } finally {
        setLoading(false);
      }
    }

    loadHome();
    return () => controller.abort();
  }, [canFetch, copy.errorTitle]);

  const heroProduct = products[0] || bestSelling[0] || discounted[0];
  const featuredNew = products[0];
  const newGrid = products.slice(1, 3);
  const bestMain = bestSelling[0];
  const saleMain = discounted[0];

  return (
    <main className="relative overflow-x-hidden bg-brand-ivory text-brand-brown">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[#f7f0e5]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(205,170,111,0.10),transparent_24%),radial-gradient(circle_at_bottom_right,rgba(43,31,26,0.05),transparent_28%)]" />
      </div>

      <section className="pt-24 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid items-center gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-10">
            <div className="rounded-[34px] border border-brand-brown/10 bg-white/78 p-6 shadow-[0_24px_80px_rgba(43,31,26,0.08)] sm:p-8 lg:p-10">
              <TinyPill icon={<Sparkles className="h-4 w-4" />}>{copy.heroBadge}</TinyPill>

              <h1 className="mt-8 max-w-3xl text-[42px] font-medium leading-[0.92] tracking-[0.01em] text-brand-brown sm:text-[58px] lg:text-[78px]">
                {copy.heroTitle}
              </h1>

              <p className="mt-5 text-sm leading-7 text-brand-brown/64 sm:text-[15px] sm:leading-8">{copy.heroDesc}</p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/collection"
                  className="inline-flex h-12 items-center gap-3 rounded-full bg-brand-brown px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-ivory transition hover:opacity-90"
                >
                  {copy.heroPrimary}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>

                <Link
                  to="/drops"
                  className="inline-flex h-12 items-center gap-3 rounded-full border border-brand-brown/12 px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/76 transition hover:border-brand-brown/24 hover:bg-brand-brown/5"
                >
                  {copy.heroSecondary}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <TinyPill icon={<Star className="h-4 w-4" />}>{copy.stats[0]}</TinyPill>
                <TinyPill icon={<Tag className="h-4 w-4" />}>{copy.stats[1]}</TinyPill>
                <TinyPill icon={<Sparkles className="h-4 w-4" />}>{copy.stats[2]}</TinyPill>
              </div>
            </div>

            <div className="overflow-hidden rounded-[34px] border border-brand-brown/10 bg-white/78 shadow-[0_34px_120px_rgba(43,31,26,0.14)]">
              <div className="relative aspect-[4/5] min-h-[420px] bg-[#f7f1e8] sm:min-h-[540px] lg:min-h-[640px]">
                <img
                  src={heroProduct?.image_url || HERO_FALLBACK}
                  alt={heroProduct?.name || "OWL DREAM"}
                  className="h-full w-full object-cover"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = HERO_FALLBACK;
                  }}
                />
                <div className="absolute left-4 top-4 sm:left-6 sm:top-6">
                  <TinyPill>{copy.quick.new}</TinyPill>
                </div>
              </div>

              <div className="border-t border-brand-brown/8 bg-white/96 p-5 sm:p-6 lg:p-7">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/48">{copy.heroProductLabel}</p>
                    <h2 className="mt-2 text-[28px] font-medium leading-[0.98] text-brand-brown sm:text-[34px] lg:text-[40px]">
                      {heroProduct?.name || copy.heroTitle}
                    </h2>
                  </div>
                  {heroProduct ? <p className="text-sm font-medium text-brand-brown/80">{formatPrice(heroProduct.base_price, lang)}</p> : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-14 pt-10 sm:pb-16 sm:pt-12 lg:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-brand-brown/46">{copy.sectionNew.kicker}</p>
              <h2 className="mt-3 text-[30px] font-medium leading-[0.98] tracking-[0.01em] text-brand-brown sm:text-[42px]">{copy.sectionNew.title}</h2>
            </div>
            <Link to="/collection" className="hidden items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown/72 transition hover:text-brand-brown md:inline-flex">
              {copy.sectionNew.cta}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
              <Skeleton className="min-h-[420px] lg:col-span-2 lg:min-h-[620px]" />
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
                <Skeleton className="h-[360px]" />
                <Skeleton className="h-[360px]" />
              </div>
            </div>
          ) : featuredNew ? (
            <div className="grid gap-5 lg:grid-cols-3 lg:gap-6">
              <ProductTile item={featuredNew} lang={lang} label={copy.quick.new} viewLabel={copy.viewProduct} featured />

              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-1">
                {newGrid.length > 0 ? (
                  newGrid.map((item) => (
                    <ProductTile key={item.product_id} item={item} lang={lang} label={copy.quick.new} viewLabel={copy.viewProduct} />
                  ))
                ) : (
                  <EmptyState title={copy.emptyTitle} desc={copy.emptyDesc} className="h-full min-h-[360px]" />
                )}
              </div>
            </div>
          ) : (
            <EmptyState title={copy.emptyTitle} desc={copy.emptyDesc} />
          )}
        </div>
      </section>

      <section className="pb-14 sm:pb-16 lg:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
            {loading ? (
              <>
                <Skeleton className="h-[520px]" />
                <Skeleton className="h-[520px]" />
              </>
            ) : (
              <>
                {bestMain ? (
                  <SplitSpotlight
                    item={bestMain}
                    lang={lang}
                    kicker={copy.sectionBest.kicker}
                    title={copy.sectionBest.title}
                    viewLabel={copy.viewProduct}
                    shopLabel={copy.shopAll}
                  />
                ) : (
                  <EmptyState title={copy.emptyTitle} desc={copy.emptyDesc} className="min-h-[520px]" />
                )}

                {saleMain ? (
                  <SplitSpotlight
                    item={saleMain}
                    lang={lang}
                    kicker={copy.sectionSale.kicker}
                    title={copy.sectionSale.title}
                    viewLabel={copy.viewProduct}
                    shopLabel={copy.shopAll}
                    dark
                    reverse
                  />
                ) : (
                  <EmptyState title={copy.emptyTitle} desc={copy.emptyDesc} dark className="min-h-[520px] bg-brand-brown text-brand-ivory" />
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="pb-16 sm:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="overflow-hidden rounded-[34px] border border-white/8 bg-brand-brown text-brand-ivory shadow-[0_40px_140px_rgba(20,14,12,0.25)]">
            <div className="grid gap-8 p-6 sm:p-8 lg:grid-cols-[0.88fr_1.12fr] lg:p-10">
              <div className="flex flex-col justify-between">
                <div>
                  <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-white/56">OWL DREAM</p>
                  <h3 className="mt-4 text-[34px] font-medium leading-[0.96] sm:text-[46px]">{copy.finalTitle}</h3>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/collection"
                    className="inline-flex h-12 items-center gap-3 rounded-full bg-white px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-brand-brown transition hover:bg-brand-ivory"
                  >
                    {copy.finalPrimary}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>

                  <Link
                    to="/drops"
                    className="inline-flex h-12 items-center gap-3 rounded-full border border-white/12 bg-white/8 px-6 text-[11px] font-medium uppercase tracking-[0.14em] text-white transition hover:bg-white/12"
                  >
                    {copy.finalSecondary}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="rounded-[26px] border border-white/10 bg-white/8 p-5">
                  <Star className="h-5 w-5 text-brand-gold" />
                  <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.16em] text-white/84">{copy.stats[0]}</p>
                </div>
                <div className="rounded-[26px] border border-white/10 bg-white/8 p-5">
                  <Tag className="h-5 w-5 text-brand-gold" />
                  <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.16em] text-white/84">{copy.stats[1]}</p>
                </div>
                <div className="rounded-[26px] border border-white/10 bg-white/8 p-5">
                  <Sparkles className="h-5 w-5 text-brand-gold" />
                  <p className="mt-6 text-[11px] font-medium uppercase tracking-[0.16em] text-white/84">{copy.stats[2]}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error && !loading ? (
        <section className="pb-16 sm:pb-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
            <div className="rounded-[28px] border border-red-500/20 bg-red-500/5 p-6">
              <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-red-700/78">{copy.errorTitle}</p>
              <p className="mt-3 text-sm leading-7 text-red-700/78">{error}</p>
              {!API_URL ? <p className="mt-3 text-xs text-red-700/70">{copy.missingApi}</p> : null}
            </div>
          </div>
        </section>
      ) : null}
    </main>
  );
}
